import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import React, { useState } from "react";

import { ViewerArtifacts } from "../../../data/queries/viewer";
import { generatePdf } from "../utils/interactionPdfExport";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  data: ViewerArtifacts | null;
  interactionId: string | null;
}

export const ShareDialog = ({
  open,
  onClose,
  data,
  interactionId
}: ShareDialogProps): React.ReactElement => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const handleToggle = (optionId: string) => {
    const currentIndex = selectedOptions.indexOf(optionId);
    const newSelected = [...selectedOptions];

    if (currentIndex === -1) {
      newSelected.push(optionId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedOptions(newSelected);
  };

  const getShareOptions = () => {
    if (!data) {
      return [];
    }

    const hasSummary = Boolean(data?.summary?.content);
    const hasExtendedSummary = Boolean(data?.summary?.content?.extendedSummary);
    const hasTranscript = Boolean(data?.transcript?.content?.transcription);

    return [
      {
        id: "summary",
        label: "Summary",
        available: hasSummary
      },
      {
        id: "extendedSummary",
        label: "Extended Summary",
        available: hasExtendedSummary
      },
      {
        id: "transcript",
        label: "Transcript",
        available: hasTranscript
      }
    ];
  };

  const handleShare = () => {
    if (!data || !interactionId) {
      return;
    }

    const callEvent = data?.callDataRecord?.content?.callEvent ||
                      data?.callRecordingEvent?.content?.callEvent ||
                      data?.callTranscriptionRecord?.content?.callEvent;

    if (!callEvent) {
      return;
    }

    generatePdf({
      data,
      selectedElements: selectedOptions,
      callEvent,
      interactionId
    });

    setSelectedOptions([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedOptions([]);
    onClose();
  };

  const options = getShareOptions();
  const availableOptions = options.filter(option => option.available);
  const hasSelection = selectedOptions.length > 0;

  const sharingMethods = [
    {
      id: "download",
      icon: <DownloadIcon />,
      title: "Download PDF",
      description: "Save to your device",
      enabled: true,
      onClick: handleShare
    }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Call</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select elements to include:
        </Typography>
        <FormGroup>
          {availableOptions.map(option => (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={selectedOptions.includes(option.id)}
                  onChange={() => handleToggle(option.id)}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
        {availableOptions.length === 0 && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No elements available to share
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose how to share:
        </Typography>

        <Stack spacing={1.5}>
          {sharingMethods.map(method => (
            <Paper
              key={method.id}
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                cursor: method.enabled && hasSelection ? "pointer" : "not-allowed",
                opacity: method.enabled && hasSelection ? 1 : 0.5,
                transition: "all 0.2s ease-in-out",
                "&:hover": method.enabled && hasSelection ? {
                  borderColor: "primary.main",
                  bgcolor: "action.hover"
                } : {},
                position: "relative"
              }}
              onClick={() => {
                if (method.enabled && hasSelection) {
                  method.onClick();
                }
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    color: method.enabled ? "primary.main" : "text.disabled"
                  }}
                >
                  {method.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={500}>
                    {method.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {method.description}
                  </Typography>
                </Box>
                {method.enabled && hasSelection && (
                  <ChevronRightIcon color="action" />
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
